/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, SelectField, SelectOption, Typography, Typography2 } from '@coursera/cds-core';
import { ErrorIcon } from '@coursera/cds-icons';

import { currentYear, earliestYear, getMonths } from 'bundles/account-profile/common/constants';
import RequiredFieldLabel from 'bundles/account-profile/components/recruiting-profile-viewer/RequiredFieldLabel';

import _t from 'i18n!nls/account-profile';

const styles = {
  leftSideRow: () => css`
    margin-right: var(--cds-spacing-150);
  `,
  rightSideRow: () => css`
    margin-left: var(--cds-spacing-150);
  `,
  withBottomMargin: () => css`
    margin-bottom: var(--cds-spacing-300);
  `,
  header: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
  formInput: css`
    label {
      margin-bottom: 0;
    }
  `,
  errorMessage: () => css`
    background-color: var(--cds-color-red-25);
    padding: var(--cds-spacing-100) var(--cds-spacing-100);
    border-radius: 4px;
  `,
  errorIcon: () => css`
    margin-right: var(--cds-spacing-100);
  `,
};

type Props = {
  selectedMonth?: number;
  selectedYear?: number;
  handleMonthChange: (value: number) => void;
  handleYearChange: (value: number) => void;
  isReadOnly?: boolean;
  title: string;
  validationStatus?: string;
  isRequired?: boolean;
  yearOffset?: number;
  'data-e2e'?: string;
};

const E2E_SELECTORS = {
  MONTH: 'date-month',
  YEAR: 'date-year',
} as const;

const ModalDateSection = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      'data-e2e': e2e,
      selectedMonth,
      selectedYear,
      handleMonthChange,
      handleYearChange,
      isReadOnly,
      title,
      validationStatus,
      isRequired,
      yearOffset,
    },
    ref
  ) => {
    const getYearsWithOffset = (offset: number) => {
      return Array.from({ length: currentYear - offset - earliestYear + 1 }, (v, k) => currentYear - offset - k);
    };

    return (
      <div data-e2e={e2e} ref={ref}>
        <Typography variant="h2semibold" css={styles.header}>
          {title}
        </Typography>
        <Grid container direction="row" wrap="nowrap" justifyContent="space-between" css={styles.withBottomMargin}>
          <Grid item xs={6} css={styles.leftSideRow}>
            <SelectField
              label={_t('Month')}
              renderLabel={(labelComponent: React.ReactElement) => (
                <div>
                  {isRequired ? <RequiredFieldLabel labelComponent={labelComponent} /> : <span>{labelComponent}</span>}
                </div>
              )}
              fullWidth
              data-e2e={E2E_SELECTORS.MONTH}
              onChange={(e) => handleMonthChange(e.target.value as number)}
              placeholder={_t('Select month')}
              readOnly={isReadOnly}
              defaultValue={selectedMonth ?? ''}
              value={selectedMonth ?? ''}
              data-testid="month-select-field"
              css={styles.formInput}
            >
              {getMonths().map((month, index) => (
                <SelectOption key={month} value={index + 1}>
                  {month}
                </SelectOption>
              ))}
            </SelectField>
          </Grid>
          <Grid item xs={6} css={styles.rightSideRow}>
            <SelectField
              label={_t('Year')}
              renderLabel={(labelComponent: React.ReactElement) => (
                <div>
                  {isRequired ? <RequiredFieldLabel labelComponent={labelComponent} /> : <span>{labelComponent}</span>}
                </div>
              )}
              fullWidth
              data-e2e={E2E_SELECTORS.YEAR}
              defaultValue={selectedYear ?? ''}
              value={selectedYear ?? ''}
              readOnly={isReadOnly}
              placeholder={_t('Select year')}
              onChange={(e) => handleYearChange(e.target.value as number)}
              css={styles.formInput}
            >
              {getYearsWithOffset(yearOffset ?? 0).map((year: number) => (
                <SelectOption key={year} value={year}>
                  {year}
                </SelectOption>
              ))}
            </SelectField>
          </Grid>
        </Grid>
        {validationStatus && (
          <Grid
            container
            direction="row"
            wrap="wrap"
            alignItems="center"
            css={[styles.errorMessage, styles.withBottomMargin]}
          >
            <ErrorIcon size="small" color="error" css={styles.errorIcon} />
            <Typography2 color="error" variant="subtitleMedium" component="span">
              {validationStatus}
            </Typography2>
          </Grid>
        )}
      </div>
    );
  }
);

export default ModalDateSection;
